import React, { useState } from 'react';
import { AlertCircle, Info } from 'lucide-react';

const PROCESSING_NOTICE = 'Quick submissions are processed by our team before being published. This can take significantly longer than a full-form submission.';

export function SimplifiedSubmitForm({ isSubmitting, onSubmit, selectedLocationName }: {
  isSubmitting: boolean; onSubmit: (d: string) => void; selectedLocationName: string;
}) {
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) { setError('Please describe your posting.'); return; }
    setError(''); onSubmit(description.trim());
  };
  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">Quick Submit</h1>
            <div className="relative" onMouseEnter={() => setTooltipVisible(true)} onMouseLeave={() => setTooltipVisible(false)}>
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
              {tooltipVisible && (
                <div className="absolute z-10 left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg">
                  <p className="font-semibold mb-1">Takes longer to appear</p>
                  <p>{PROCESSING_NOTICE}</p>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">Location: {selectedLocationName}</p>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Describe your posting <span className="text-red-500">*</span></label>
          <p className="text-xs text-gray-400 mb-3">Include as much detail as you can. Our team will use this to categorise and publish your post.</p>
          <textarea value={description} onChange={(e) => { setDescription(e.target.value); if (error) setError(''); }} rows={7}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-colors resize-none ${error ? 'border-red-300 bg-red-50' : 'border-input'}`}
            placeholder="Describe what you're posting in as much detail as possible..." />
          {error && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {error}</p>}
          <p className="text-xs text-gray-400 mt-1.5 text-right">{description.length} characters</p>
        </div>
        <div className="flex items-center justify-between mt-6 gap-3">
          <div className="text-xs text-gray-500">Share the essentials and we will help categorize the post.</div>
          <button type="submit" disabled={isSubmitting || !description.trim()} className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm">
            {isSubmitting ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Submitting...</> : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function SubmissionModeSwitch({ activeMode, onSelectMode }: {
  activeMode: 'full' | 'simplified'; onSelectMode: (m: 'full' | 'simplified') => void;
}) {
  return (
    <div className="w-full max-w-2xl mx-auto px-4 pt-6">
      <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
        <button
          type="button"
          onClick={() => onSelectMode('simplified')}
          className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${activeMode === 'simplified' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Quick Submit
        </button>
        <button
          type="button"
          onClick={() => onSelectMode('full')}
          className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${activeMode === 'full' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Standard Form
        </button>
      </div>
    </div>
  );
}



